// eslint-disable-next-line no-restricted-imports
import { Percent, TradeType } from '@uniswap/sdk-core'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { PayCallbackState, usePayCallback as useLibPayCallBack } from 'lib/hooks/pay/usePayCallback'
import { ReactNode, useMemo } from 'react'

import { TransactionType } from '../state/transactions/actions'
import { useTransactionAdder } from '../state/transactions/hooks'
import { currencyId } from '../utils/currencyId'
import useENS from './useENS'
import { SignatureData } from './useERC20Permit'
import { AnyTrade } from './usePayCallArguments'
import useTransactionDeadline from './useTransactionDeadline'

// returns a function that will execute a pay, if the parameters are all valid
// and the user has approved the slippage adjusted input amount for the trade
export function usePayCallback(
  trade: AnyTrade | undefined, // trade to execute, required
  allowedSlippage: Percent, // in bips
  recipientAddressOrName: string | null, // the ENS name or address of the recipient of the trade, or null if pay should be returned to sender
  signatureData: SignatureData | undefined | null,
  paymentDetails: any // merchant contract address
): { state: PayCallbackState; callback: null | (() => Promise<string>); error: ReactNode | null } {
  const { account } = useActiveWeb3React()

  const deadline = useTransactionDeadline()

  const addTransaction = useTransactionAdder()

  const { address: recipientAddress } = useENS(recipientAddressOrName)
  const recipient = recipientAddressOrName === null ? account : recipientAddress

  const merchantId = paymentDetails?.contract_merchant_id

  const merchantReferenceId = paymentDetails?.merchant_reference_id

  const {
    state,
    callback: libCallback,
    error,
  } = useLibPayCallBack({
    trade,
    allowedSlippage,
    recipientAddressOrName: recipient,
    signatureData,
    deadline,
    merchantId,
    merchantReferenceId,
  })

  const callback = useMemo(() => {
    if (!libCallback || !trade) {
      return null
    }
    return () =>
      libCallback().then((response) => {
        addTransaction(
          response,
          trade.tradeType === TradeType.EXACT_INPUT
            ? {
                type: TransactionType.PAY,
                tradeType: TradeType.EXACT_INPUT,
                inputCurrencyId: currencyId(trade.inputAmount.currency),
                inputCurrencyAmountRaw: trade.inputAmount.quotient.toString(),
                expectedOutputCurrencyAmountRaw: trade.outputAmount.quotient.toString(),
                outputCurrencyId: currencyId(trade.outputAmount.currency),
                minimumOutputCurrencyAmountRaw: trade.minimumAmountOut(allowedSlippage).quotient.toString(),
                transactionAmount: paymentDetails?.amount,
                transactionCurrency: paymentDetails?.currency,
                companyName: paymentDetails?.company_name,
              }
            : {
                type: TransactionType.PAY,
                tradeType: TradeType.EXACT_OUTPUT,
                inputCurrencyId: currencyId(trade.inputAmount.currency),
                maximumInputCurrencyAmountRaw: trade.maximumAmountIn(allowedSlippage).quotient.toString(),
                outputCurrencyId: currencyId(trade.outputAmount.currency),
                outputCurrencyAmountRaw: trade.outputAmount.quotient.toString(),
                expectedInputCurrencyAmountRaw: trade.inputAmount.quotient.toString(),
                transactionAmount: paymentDetails?.amount,
                transactionCurrency: paymentDetails?.currency,
                companyName: paymentDetails?.company_name,
              }
        )
        return response.hash
      })
  }, [addTransaction, allowedSlippage, libCallback, trade, paymentDetails])

  return {
    state,
    callback,
    error,
  }
}
